import { useNavigation } from '@react-navigation/native';
import { isDateAfterLimit } from '_/helpers/isDateAfterLimit';
import { isAfter } from 'date-fns';

import { AppRoute, CheckinProps } from './../navigation/types';
import { useVisit } from './VisitContext';
import { useCentralizedValuesFallback } from './useCentralizedValuesFallback';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import { colors } from '_/config/theme';
import { useIsDesktopConnected } from './useIsDesktopConnected';
import { VisitFlowTypesModel } from '@w3lcome/types';

const CHECKIN_FLOW = [
  AppRoute.CHECKIN,
  AppRoute.CHECKIN_INFORMATION,
  AppRoute.CHECKIN_HOST,
  AppRoute.CHECKIN_EXTRA_HOST_FORM,
  AppRoute.CHECKIN_PICTURE,
  AppRoute.CHECKIN_NDA,
  AppRoute.CHECKIN_QUESTIONNARIE,
  AppRoute.CHECKIN_MESSAGE,
];

export function useNextCheckinScreen(currentScreen: AppRoute) {
  const { visit } = useVisit();
  const { navigate } = useNavigation<CheckinProps>();
  const { t } = useTranslation();

  const desktopConnected = useIsDesktopConnected();

  function isScreenAfterCurrent(screen: AppRoute) {
    return (
      CHECKIN_FLOW.findIndex((route) => route === screen) >
      CHECKIN_FLOW.findIndex((route) => route === currentScreen)
    );
  }

  const { selectedOrFallbackVisitFlowType, selectedOrFallbackCustomization } =
    useCentralizedValuesFallback();

  function needAnswerHostForm(visitFlowTypeParam: VisitFlowTypesModel) {
    const hostFormRequiredFields =
      visitFlowTypeParam?.extraHostFormFields?.filter((f) => f.required) || [];

    return (
      !visit.expectedAt ||
      hostFormRequiredFields.some(
        (field) => !visit?.extraHostFormFields?.find((vf) => vf.customFieldId === field.id)
      )
    );
  }

  function goToNextScreen(visitFlowTypeParam?: VisitFlowTypesModel) {
    const flowTypeToUse = visitFlowTypeParam || selectedOrFallbackVisitFlowType;

    if (!desktopConnected && Platform.OS === 'web') {
      showMessage({
        message: t('error'),
        description: t('homeScreen.Connection'),
        backgroundColor: colors.danger,
      });
      return;
    }

    if (visit.isAnonymous) {
      if (isScreenAfterCurrent(AppRoute.CHECKIN_INFORMATION) && flowTypeToUse?.hostEnabled) {
        if (!visit.hostId) {
          return navigate(AppRoute.CHECKIN_HOST);
        }
      }

      return navigate(AppRoute.CHECKIN_MESSAGE);
    }

    if (isScreenAfterCurrent(AppRoute.CHECKIN_HOST) && flowTypeToUse?.hostEnabled) {
      if (!visit.hostId) {
        return navigate(AppRoute.CHECKIN_HOST);
      }
    }

    if (
      isScreenAfterCurrent(AppRoute.CHECKIN_EXTRA_HOST_FORM) &&
      flowTypeToUse?.displayHostExtraInfoForm &&
      flowTypeToUse?.extraHostFormFields &&
      flowTypeToUse.extraHostFormFields?.length > 0 &&
      needAnswerHostForm(flowTypeToUse)
    ) {
      return navigate(AppRoute.CHECKIN_EXTRA_HOST_FORM);
    }

    if (isScreenAfterCurrent(AppRoute.CHECKIN_PICTURE) && flowTypeToUse?.pictureEnabled) {
      const preRegistrationWithoutPicture =
        visit.expectedAt &&
        (!visit.pictureUrl || selectedOrFallbackCustomization?.takePictureOnlyOnKiosk);
      const visitWithoutPictureOrUniquePicture =
        !visit.expectedAt && (!visit.pictureUrl || !flowTypeToUse?.pictureUnique);

      if (preRegistrationWithoutPicture || visitWithoutPictureOrUniquePicture) {
        return navigate(AppRoute.CHECKIN_PICTURE);
      }
    }

    if (isScreenAfterCurrent(AppRoute.CHECKIN_NDA) && flowTypeToUse?.ndaEnabled) {
      const isNDAAfterLimit = isDateAfterLimit(
        flowTypeToUse?.ndaValidForDays,
        visit?.ndaSignedAt as unknown as string
      );

      const ndaLastEdited = new Date(flowTypeToUse?.ndaLastEdited ?? 0);
      const ndaSignedAt = new Date(visit?.ndaSignedAt ?? 0);

      const isAfterNdaLastEdited = isAfter(ndaLastEdited, ndaSignedAt);

      const preRegistrationWithoutNDA = visit.expectedAt && !visit.ndaUrl;
      const visitWithoutNDAorAfterLimit = !visit.expectedAt && (!visit.ndaUrl || isNDAAfterLimit);

      if (preRegistrationWithoutNDA || visitWithoutNDAorAfterLimit || isAfterNdaLastEdited) {
        return navigate(AppRoute.CHECKIN_NDA);
      }
    }

    if (
      isScreenAfterCurrent(AppRoute.CHECKIN_QUESTIONNARIE) &&
      flowTypeToUse?.questionnaireEnabled &&
      flowTypeToUse?.questionnaireFields &&
      flowTypeToUse?.questionnaireFields?.length > 0
    ) {
      const isQuestionnaireAfterLimit = isDateAfterLimit(
        flowTypeToUse?.questionnaireValidForDays,
        visit?.questionnaireAnsweredAt as unknown as string
      );

      const isQuestionnaireLastEditedAfterVisitAnswer = isAfter(
        new Date(flowTypeToUse?.questionnaireLastEdited as unknown as string),
        new Date(visit?.questionnaireAnsweredAt as unknown as string)
      );

      const visitWithoutQuestionnaireOrAfterLimitOrEdited =
        !visit.expectedAt &&
        (!visit?.questionnaireAnsweredAt ||
          isQuestionnaireAfterLimit ||
          isQuestionnaireLastEditedAfterVisitAnswer);
      const preRegistrationQuestionnaireEditedAfterAnswered =
        visit.expectedAt &&
        (visit.questionnaireAnsweredAt || '') < (flowTypeToUse.questionnaireLastEdited || '');

      if (
        visitWithoutQuestionnaireOrAfterLimitOrEdited ||
        preRegistrationQuestionnaireEditedAfterAnswered
      ) {
        return navigate(AppRoute.CHECKIN_QUESTIONNARIE);
      }
    }

    if (
      isScreenAfterCurrent(AppRoute.EXTRA_PRINT_SCREENS) &&
      flowTypeToUse?.extraSuccessScreenEnabled
    ) {
      return navigate(AppRoute.EXTRA_PRINT_SCREENS);
    }

    navigate(AppRoute.CHECKIN_MESSAGE);
  }

  return { goToNextScreen };
}
